import { createApi, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'

import { purchaseResponse, UserItems } from '~/components/Modals/GemShop/GemShop.types'
import { AppLanguage } from '~/types/locale'
import { getFirebase } from '~/firebase'
import { apiPrivate } from '~/redux/utils/hom-api'
import { gemShopSanity } from '~/shared/sanity/gem-shop'
import { GemShopInventory } from '~/types/gem-shop'

export const gemShopApi = createApi({
  reducerPath: 'gemShopApi',
  tagTypes: ['GemShop'],
  baseQuery: apiPrivate,
  endpoints: (build) => ({
    getGemStoreData: build.query<GemShopInventory, { language: AppLanguage; dragonEvolution: string }>({
      queryFn: async ({ language, dragonEvolution }) => {
        try {
          const data = await gemShopSanity.getGemShopItems(language as AppLanguage, dragonEvolution)

          return { data }
        } catch (error) {
          return { error: error as FetchBaseQueryError }
        }
      },
    }),

    getUserItems: build.query<UserItems, void>({
      queryFn: async () => {
        const { httpsCallable, functions } = await getFirebase()
        const response = await httpsCallable<void, UserItems>(functions, 'getUserItems')()
        return {
          data: response.data,
        }
      },
      providesTags: ['GemShop'],
    }),

    buyItem: build.mutation<purchaseResponse, string>({
      queryFn: async (itemSku) => {
        const { httpsCallable, functions } = await getFirebase()
        const status = await httpsCallable<{ itemSku: string }, { success: boolean }>(
          functions,
          'buyItem',
        )({
          itemSku,
        })
        return {
          data: status.data,
        }
      },
      invalidatesTags: ['GemShop'],
    }),

    upgradeItem: build.mutation<purchaseResponse, string>({
      queryFn: async (itemSku) => {
        const { httpsCallable, functions } = await getFirebase()
        const status = await httpsCallable<{ itemSku: string }, { success: boolean }>(
          functions,
          'upgradeItem',
        )({
          itemSku,
        })
        return {
          data: status.data,
        }
      },
      invalidatesTags: ['GemShop'],
    }),

    equipItem: build.mutation<purchaseResponse, string>({
      queryFn: async (itemSku) => {
        const { httpsCallable, functions } = await getFirebase()
        const status = await httpsCallable<{ itemSku: string }, { success: boolean }>(
          functions,
          'equipItem',
        )({
          itemSku,
        })
        return {
          data: status.data,
        }
      },
      invalidatesTags: ['GemShop'],
    }),
    unequipItem: build.mutation<purchaseResponse, string>({
      queryFn: async (itemSku) => {
        const { httpsCallable, functions } = await getFirebase()
        const status = await httpsCallable<{ itemSku: string }, { success: boolean }>(
          functions,
          'unEquipItem',
        )({
          itemSku,
        })
        return {
          data: status.data,
        }
      },
      invalidatesTags: ['GemShop'],
    }),
  }),
})

export const {
  useGetUserItemsQuery,
  useLazyGetGemStoreDataQuery,
  useGetGemStoreDataQuery,
  useBuyItemMutation,
  useUpgradeItemMutation,
  useEquipItemMutation,
  useUnequipItemMutation,
} = gemShopApi
